// Barn App — white-label tenant registry.
//
// The spine of the white-label factory (see BARN_APP_WHITELABEL_PLAN.md).
// ONE codebase; each barn is a row of config here. A build is locked to a
// single barn at compile time via `VITE_BARN_APP=<slug>`, and the whole app
// scopes its data + branding to that barn.
//
// Adding a barn = add a BarnAppConfig entry here + their brand assets +
// a per-barn Capacitor build recipe. No new app code (unless they commission
// a custom module — those are gated by `customModules` below).

export interface BarnAppColors {
  /** Main brand color — headers, primary buttons, key accents. */
  primary: string;
  /** Darker brand variant — chrome, app bars, dark backgrounds. */
  primaryDark: string;
  /** Secondary accent — prices, highlights, "live" chips. */
  accent: string;
  /** Primary text color on light surfaces. */
  ink: string;
  /** Light page background. */
  paper: string;
}

export interface BarnAppBrand {
  /** Full name shown in the app + store listing. */
  displayName: string;
  /** Compact name for tight spots (app bar, home tile). */
  shortName: string;
  tagline?: string;
  /** Path/URL to the barn's logo for LIGHT surfaces (cream/white). `null` →
   *  text wordmark fallback until the real asset is dropped in. */
  logoSrc: string | null;
  /** Reverse (light-ink) logo for DARK surfaces — app bar, splash, navy
   *  chrome. `null` → fall back to `logoSrc` (or wordmark). */
  logoReverseSrc: string | null;
  /** Masthead photo behind the centered logo (washed to white on light
   *  surfaces). Omit → the shared cattle-at-sunset hero. */
  heroSrc?: string;
  /** The masthead artwork already contains the barn's wordmark, so the app must
   *  not print it a second time. Changes two things, and both are required:
   *
   *  1. The masthead normally overlays the cream logo dead centre. On a baked-in
   *     wordmark it lands almost exactly on top and turns both to mush, so the
   *     overlay is suppressed. The barn's name still reaches screen readers via
   *     the tagline and document title.
   *  2. The standard veil is ~72–84% navy. It exists purely to make a cream logo
   *     readable over a photo; with no logo to protect it just buries artwork
   *     that is already legible. Reduced to a light top-and-bottom scrim.
   *
   *  Leave unset for an ordinary photo masthead — the logo and full veil are
   *  right for those, and a photo without a wordmark needs both. */
  heroWordmarkInImage?: boolean;
  /** A WHITE masthead carrying the barn's DARK logo, instead of a photo (or a
   *  dark ground) carrying the cream reverse. For a barn whose mark is pure
   *  black with no brand hue — Flint Hills (owner, 2026-10-08: "We can just
   *  have a white header with this logo"). Changes the masthead, the launch
   *  splash and the settings chip; every other dark surface (web page aside,
   *  share card) keeps using `logoReverseSrc`. */
  mastheadTone?: "light";
  colors: BarnAppColors;
  /** Store identity — each barn ships under its OWN developer account. */
  iosBundleId: string;
  androidPackage: string;
  /** The developer name EXACTLY as the store listing shows it (Google Play's
   *  "Developer" line). Play rejects a privacy policy or deletion page that
   *  names neither the app nor this entity, so the per-barn legal pages
   *  (/barn-privacy|terms|deletion/<slug>) print it when it is set. Leave it
   *  unset rather than guess: a wrong name is worse than the app name alone. */
  storeDeveloperName?: string;
  /** Live store links, added the day each app is approved. Until then the web
   *  home page shows "coming soon" instead of a button that goes nowhere —
   *  the page is textable from day one, so a dead button would be seen. */
  iosAppStoreUrl?: string;
  androidStoreUrl?: string;
}

/** Standard, config-toggled features every barn app can turn on/off.
 *  Bespoke barn-specific work is NOT a feature flag — it's a `customModules`
 *  id so it only compiles into the barns that commissioned it. */
export type BarnAppFeature =
  | "barn_page"    // branded public sale-results page
  | "my_barn"      // private owner/staff analytics dashboard
  | "schedule"     // sale calendar / upcoming sales
  | "weather"      // local forecast for the barn's yard (NWS)
  | "ask"          // "Ask [barn]" front-desk assistant (ask-barn fn)
  | "futures"      // CME futures link (National Beef Wire), in-app browser
  | "push"         // "sale starting" / "results posted" notifications
  | "recap"        // one-tap sale-recap share
  | "consignors"   // consignor list
  | "buyers";      // buyer list

/** One sale barn inside a multi-yard company's single app. See `yards`. */
export interface BarnYard {
  /** Stable id for the picker and for remembering the choice. Not a tenant. */
  id: string;
  /** EXACT name in dv_sale_market_reports — the thing that scopes every query. */
  barnName: string;
  /** What the picker shows. Short: it sits in a row of six on a phone. */
  label: string;
  /** Two-letter state, carried so the selected yard can replace both fields. */
  state: string;
  /**
   * ⚠️ THIS YARD'S OWN OFFICE NUMBER. A man on the Richmond board who taps
   * "Call the office" must reach RICHMOND.
   *
   * Before this existed the Contact screen was half-switched, which is worse
   * than wholly wrong: the city and the map pin followed the yard (they are
   * keyed on barnName) while the phone and website stayed on the company
   * block — so the screen said Richmond and dialled Lexington. Nothing on it
   * looked broken.
   *
   * Quote it from the barn's own location page. A wrong number in a barn's
   * own app is the one fact a man acts on immediately.
   */
  phone?: string;
  /** This yard's own page on the barn's site. Same rule as `phone`. */
  website?: string;
}

export interface BarnAppConfig {
  /** URL/CLI-safe tenant id. Used as VITE_BARN_APP value + build flavor key. */
  slug: string;
  /** Public web address for this barn's app home page, WITHOUT the leading
   *  slash — e.g. "mitchell" serves the app at raisedbeef.com/mitchell. This
   *  is the link a barn texts its customers, so it is chosen to be short and
   *  sayable rather than to match `slug`.
   *
   *  OPT-IN: omit it and the barn has NO public web page. There is deliberately
   *  no fallback to `slug` — that would publish every tenant in the registry,
   *  demo brands included, the moment this shipped.
   *
   *  ⚠️ MUST NOT collide with a platform route. A barn taking "sales" would
   *  knock out the real /sales page for every visitor on the domain, and the
   *  failure would look like a routing bug rather than a registry mistake.
   *  registry.test.ts asserts this against RESERVED_WEB_PATHS. */
  webPath?: string;
  /** EXACT barn name as stored in the database (dv_sale_market_reports /
   *  barn_geocodes / sales). This is what scopes every query to this barn.
   *
   *  ⚠️ ON A MULTI-YARD BARN THIS IS THE DEFAULT YARD, NOT THE ONLY ONE.
   *  See `yards` below: activeBarnApp() rewrites this field to whichever yard
   *  the customer has selected, so the 64 screens that read it keep working
   *  without knowing yards exist. */
  barnName: string;
  state: string;
  /**
   * ONE COMPANY, SEVERAL SALE BARNS, ONE APP.
   *
   * Bluegrass Stockyards runs six yards in Kentucky. The factory's default
   * shape — one barn, one app — would make that six near-identical apps
   * published from ONE developer account, differing only by location. That is
   * precisely the pattern Guideline 4.3 exists to reject, and the defence
   * written in BARN_APP_WHITELABEL_PLAN.md does not hold for it: of the three
   * legs, "submitted from the barn's own account" is unchanged but "genuinely
   * distinct content" and "distinct branding" both collapse when the same
   * company ships the same app six times.
   *
   * So a multi-yard barn ships ONE app with a yard picker. Safer on 4.3 (there
   * is no clone pattern at all), one review instead of six, one listing to
   * maintain — and better for the rancher, who hauls to more than one of their
   * yards.
   *
   * ⚠️ EACH ENTRY'S barnName MUST MATCH dv_sale_market_reports EXACTLY, the
   * same hard rule as the top-level one. A typo here yields a yard that looks
   * real in the picker and is empty when tapped.
   *
   * ⚠️ YARDS ARE NOT REGISTRY ENTRIES. They have no slug of their own in
   * BARN_APPS, no bundle id, no webPath — because they are not apps. One app,
   * one store identity, one address. registry.test.ts asserts those per entry
   * and would be measuring the wrong thing if a yard were a tenant.
   */
  yards?: readonly BarnYard[];
  /** Home-screen layout. `"list"` (default) is the launchpad with a sale card +
   *  Explore rows. `"grid"` is the icon-tile grid launchpad. Styling/layout only —
   *  both surface the exact same tools, data, and math. Lets a barn be offered a
   *  second app in a different layout (same brand) without any feature change. */
  layout?: "list" | "grid";
  /** The neutral sales tenant, which renders a REAL barn's data under a generic
   *  brand and must never reach a store. Every other barn is at the same stage as
   *  every other: none is active, none has been submitted.
   *
   *  A previous version of this field tried to sort barns into `client` and
   *  `prospect`. That was wrong — it inferred a commercial position the repo has
   *  no business asserting, and it got it from the wrong evidence. What actually
   *  differs between barns is whether their ARTWORK has arrived, which is a file
   *  check, not a judgement: scripts/build-barn.mjs refuses a native build when
   *  the five icon and splash assets are missing, because that build would
   *  produce an installable app carrying a real barn's name with no icon. */
  demoTenant?: true;
  brand: BarnAppBrand;
  features: BarnAppFeature[];
  /** Ids of barn-commissioned custom modules switched on for this barn only. */
  customModules: string[];
  /** Optional public contact details for the Contact screen. Phone/website
   *  aren't in our market data — the barn provides them. Directions use the
   *  geocoded yard location, so they work without this. */
  contact?: { phone?: string; website?: string; address?: string };
  /** The barn's STANDING landing page at its broadcaster — the fallback the
   *  "Video" tab opens when there is no fresh link (opens in the in-app
   *  browser). Omit to hide the tab.
   *
   *  ⚠️ NEVER AN EVENT PAGE. THIS FIELD'S DOC USED TO SAY "persistent DVAuction
   *  channel" AND THERE IS NO SUCH THING. DVAuction mints a new address for
   *  every sale (/events/276283-beaver-county-10-13-26) and has no per-seller
   *  page at all — /markets, /channels, /sellers, /barns and /directory all 404.
   *  Beaver's value was `/events/131006`, and by 2026-10-02 it redirected to
   *  DVAuction's homepage: the Watch Live button dropped a buyer on a national
   *  sale list during Beaver's own sale, and nothing errored.
   *
   *  So this must be a page that CANNOT rot — a schedule or a channel. The
   *  per-sale address lives in `barn_accounts.live_url`, which the barn sets
   *  itself in My Barn → Live Sale Link and which carries the sale date so it
   *  expires on its own. See src/barnApps/liveSaleLink.ts. */
  dvauctionUrl?: string;
  /** The barn's Facebook page. Adds a "Facebook" Explore row that opens the page
   *  in the in-app browser (real feed in the user's own session — no FB tracking
   *  embedded in our app). Omit to hide it. */
  facebookUrl?: string;
  /** The barn's OWN IANA timezone, when the state's majority zone is wrong
   *  for it. `barnTimezone(state, timezone)` prefers this; without it the
   *  app takes the state's predominant zone (`src/lib/barnTimezone.ts`), which
   *  is one hour off for a barn in a split state's minority sliver — western
   *  SD / NE / KS, the Texas panhandle, northern Idaho. St. Onge (Mountain,
   *  in a state mapped to Central) was the first: its "today", live polling
   *  and lot times all ran an hour early until this was set (2026-10-14).
   *  Every screen that resolves the barn's clock must pass it —
   *  barnTimezoneWiring.test.ts checks that no call site drops it. */
  timezone?: string;
  /** The barn's TikTok profile. Adds a "TikTok" row/tile that opens the profile
   *  in the in-app browser — the customer's own TikTok session, so the barn gets
   *  the view, the follow and the comments where they count.
   *
   *  SAME STANCE AS facebookUrl, deliberately: a LINK, not an embed. TikTok's
   *  embed ships their tracking, which would change the App Store privacy
   *  answers and Play Data Safety declarations already submitted for every barn
   *  app, and likely the age rating too. Owner decision 2026-08-13: keep
   *  third-party tracking out of the app.
   *
   *  Omit to hide it. Note the scoped-down `beaver-county-dvauction` edition
   *  lists its tiles explicitly, so this will NOT appear there unless "tiktok"
   *  is added to that list — which is the intent for now. */
  tiktokUrl?: string;
  /** The barn's OWN YouTube channel id — "UC" + 22 characters, NOT the
   *  @handle. Read by the feeder-flash-videos edge function, which lists a
   *  channel's latest uploads.
   *
   *  Get it by loading the channel page and reading `externalId` out of the
   *  HTML; the handle alone is not enough for the API. Verify it belongs to the
   *  barn before adding — a wrong id here silently shows another channel's
   *  videos under the barn's name.
   *
   *  NOTE: this needs the YOUTUBE_API_KEY secret to be set. Without it the
   *  function falls back to YouTube's free RSS feed, which is rate-limited by IP
   *  and was returning 404 for every channel on 2026-08-14.
   *
   *  ⚠️ ALSO SET IT IN THE DATABASE. `public.barn_channel_sync` holds the same
   *  id server-side, because the poller that turns a new upload into a push
   *  notification is an edge function and cannot import this module. Setting it
   *  here alone gives the barn a live video feed in the app but NO notification
   *  when it posts. The two can drift; a mismatch degrades to "no push", never
   *  to the wrong barn's video. See migration 20260818000001_barn_channel_sync. */
  youtubeChannelId?: string;
  /** The barn's OWN SMS text-alert list (they run it — we just make opt-in one
   *  tap). `keyword` is texted to `number` (digits only). Adds a Text Alerts tool
   *  that opens the phone's messaging app pre-filled. Omit to hide the tool. */
  textAlerts?: { keyword: string; number: string };
  /** The barn's studio show/podcast. Adds a branded "Show" tab (cover + episodes)
   *  that presents the barn's video posts as a program. `coverSrc` (a studio
   *  photo) is the hero — falls back to a styled gradient if omitted. When set,
   *  the Home video entry becomes the branded Show. Omit to hide it. */
  show?: { title: string; tagline?: string; coverSrc?: string };
  /** Optional tile allow-list (home-grid tile keys). When set, the grid AND the
   *  bottom tab bar show ONLY these tiles, in THIS order — everything else is
   *  hidden. `settings` and `mybarn` are always-on plumbing (never filtered out;
   *  My Barn keeps its own owner gate). This can only REMOVE tiles, never force
   *  one whose data/flag is missing. Used to ship a scoped-down edition of a barn
   *  (e.g. the DVAuction core set) with the rest held back as "premium, added
   *  later" — flip a tool back on by adding its key here. Omit → show everything.
   *
   *  Valid keys (the `key:` values in BarnAppHomeGrid — asserted by
   *  registry.test.ts, so an invented key fails the build rather than silently
   *  doing nothing):
   *    calftracker, consign, contact, dvauction, facebook, feederflash,
   *    feedertop, forecast, futures, mybarn, newswire, priceboard, records,
   *    salereport, salesheet, schedule, show, textalerts, ticker, tiktok,
   *    topfive, topnational, video, wavg, weather
   *
   *  ⚠️ This list controls the HOME SCREEN and TAB BAR only. Buyer alerts and
   *  Deal alerts live in **Settings** and are NOT tiles — they render for every
   *  barn regardless of what is listed here, so a scoped-down edition does not
   *  hide them. (Corrected 2026-08-11: this comment previously named
   *  `buyeralerts`/`lotalerts` as valid tile keys. They never were — listing
   *  them did nothing at all.) */
  tiles?: string[];
  /** Optional per-tile label overrides (tile key → label). Lets a barn rename a
   *  tile to its preferred wording without changing it for other barns.
   *  Honored by both home layouts. (Beaver used to relabel `feedertop`
   *  "Leaderboard" here; since 2026-10-13 that is the default for every barn,
   *  so no barn overrides it any more.) */
  tileLabels?: Record<string, string>;
  /** Optional "pin to front" list (tile keys). These tiles are moved to the FRONT
   *  of the home screen — the top row of the grid, or the top of the list — in this
   *  order, ahead of everything else. Does not hide anything; unlisted tiles keep
   *  their normal order behind the pinned ones. Honored by both home layouts. */
  tilesFirst?: string[];
  /** Which cattle types the **Price Board** toggle offers, in order. Omit to
   *  offer all four (`NATIONAL_CATTLE_TYPES`), which stays the default.
   *
   *  ⚠️ THIS IS PER BARN AND MUST STAY PER BARN. Cows and Bulls were ADDED to
   *  this toggle on 2026-08-14 precisely because a cow/bull barn was being shown
   *  the wrong business — Mitchell Livestock ran 2,500 head of cows against
   *  1,206 steers over six weeks while their board opened on an empty steer
   *  column. Narrowing this globally would reinstate that bug for every cull
   *  barn in the factory. Set it only on a barn that genuinely sells one side.
   *
   *  The first entry is the type the board opens on. */
  priceBoardTypes?: string[];
  /** Which sale types the app shows PRICES for (src/barnApps/saleScope.ts,
   *  2026-11-02): the latest-sale cards, Sales and Market Reports open only on
   *  these. Schedule, Forecast, Ask and Consign still list every sale day; the
   *  Schedule shows a hidden-type sale but won't open it. Omit to show every sale the barn ran, which
   *  stays the default. Pair it with `priceBoardTypes`. Push notifications are
   *  scoped separately, server-side, by barn_accounts.push_sale_types.
   *
   *  Values match `sales.sale_type`: `feeder`, `cow_bull`, `mixed`, `special`,
   *  `unclassified`.
   *
   *  ⚠️ SAME WARNING AS ABOVE, and sharper: a cow/bull barn restricted to
   *  `["feeder"]` gets an EMPTY picker and a Market Reports screen that can
   *  never show anything. (This said "every one of Mitchell's sales is
   *  `cow_bull`"; by 2026-11-02 it ran 19 feeder sales in a year.) Verify a
   *  barn actually runs the types listed here before setting it — Beaver's split
   *  is 25 feeder (Tuesdays) / 25 cow_bull (Mondays), checked 2026-10-08. */
  saleTypes?: string[];
  /** Put the barn's newest video at the TOP of the home screen — a playable hero
   *  card plus a rail of recent uploads, above the Assistant and the tiles.
   *  Omit (or false) for the classic home: tools first, video behind its tile.
   *
   *  Worth turning on only for a barn that actually posts. Beaver County put up
   *  twelve clips in the five days to 2026-08-18 — four of them that day — which
   *  is a faster refresh than any market source in the product, so their channel
   *  earns the top of the screen. A barn that posts monthly would get a stale
   *  billboard where its tools used to be, which is worse than no hero at all.
   *  (The component renders nothing when a barn has no videos, so a quiet barn
   *  degrades to today's layout rather than to an empty box — but "nothing
   *  breaks" is not the same as "this is a good idea for that barn".)
   *
   *  PREVIEW BOTH VERSIONS WITHOUT A CODE CHANGE: `?videohero=1` forces it on
   *  and `?videohero=0` forces it off, for any barn. That is how to show a barn
   *  the two layouts side by side before they choose — see resolveVideoHero(). */
  videoHero?: boolean;
  /** Show the "DVAuction · Powered by RaisedBeef" lockup under the header of the
   *  DVAuction-data tools (Leaderboard, Ticker, Calf Tracker, Records, Top
   *  National, Market Reports, Weighted Averages, Price Board) — not just on Home.
   *
   *  Deliberately opt-in per barn rather than on by default. The lockup already
   *  appears on EVERY barn's home screen, so switching this on globally would
   *  visibly change 24 apps at once, several already submitted to stores. The
   *  credit is the same either way; the blast radius is not.
   *
   *  These eight tools are the ones whose numbers come from DVAuction auction
   *  data, which is the whole reason the credit belongs on them. If a tool is
   *  added to that list in BarnAppShell's DV_TOOL_SCREENS, it should be because
   *  it reads DV data — not because it looked lonely. */
  partnerCredit?: boolean;
  /** Over-the-air updates for THIS barn. Gate 2 of 2 — see src/barnApps/ota.ts
   *  and BARN_APP_OTA_SCOPE.md. Gate 1 is `autoUpdate: false` in every
   *  capacitor.config.ts.
   *
   *  Default off, and it must STAY off for every barn until Stage B has been
   *  tested end-to-end against a real Capgo account on a real device. Enabling
   *  it is the point at which we can push code onto a barn's customers' phones,
   *  which is not a flag to flip speculatively.
   *
   *  The channel is derived from the slug (`barn-<slug>`), never written here —
   *  a hand-typed channel is how one barn's bundle lands on another barn's
   *  phones. */
  ota?: { enabled?: boolean };
}

// ─── Tenants ────────────────────────────────────────────────────────────────

export const BARN_APPS: Record<string, BarnAppConfig> = {
  // Barn #1 — the Phase-1 reference build.
  // Brand colors sampled from Beaver County's own logo (navy-teal, monochrome).
  // The `accent` gold is a derived complement (their mark carries no second
  // color) — easy to change if they want a different highlight.
  "beaver-county": {
    slug: "beaver-county",
    webPath: "beaver-county",             // raisedbeef.com/beaver-county
    // GRID IS THE STANDARD (owner, 2026-08-15). Beaver and Mitchell were the last
    // two barns on the list layout; the other 20 already shipped as grid. One
    // layout across the factory means one thing to test, screenshot and support.
    layout: "grid",
    barnName: "Beaver County Stockyards", // verified exact DB name 2026-07-20
    state: "OK",
    brand: {
      displayName: "Beaver County Stockyards",
      shortName: "Beaver County",
      tagline: "Beaver, Oklahoma",
      logoSrc: "/barn-apps/beaver-county/logo-hd.png",        // navy art (hi-res, white knocked out), for light surfaces
      // THE BARN'S OWN white wordmark, supplied by them 2026-09-11 and trimmed
      // to its artwork bounds (1280x469). What sat here before was a REDRAW —
      // same words, but an angular geometric longhorn and a clean star bar,
      // where Beaver's actual mark has an organic outline longhorn and a
      // distressed bar. The app had been showing an imitation of their logo.
      logoReverseSrc: "/barn-apps/beaver-county/logo-reverse-hd.png",
      // Flat charcoal, matching the chrome and the splash. It replaced the neon
      // sale-ring wall (`masthead-neon.jpg`, kept in the folder), which the owner
      // chose on 2026-08-19 — before anyone had seen it at phone size. On an
      // iPhone 17 Pro Max its small signage reads as text-shaped noise
      // ("WIENVER FEED", "O EXIT & EXIT EDW") and one sign resolves to legible
      // words that name a business that does not exist. A masthead is where the
      // barn's name lives; it should carry their mark, not invented signage.
      // `masthead.jpg` (Angus photo) is also still there if they want a picture.
      heroSrc: "/barn-apps/beaver-county/masthead-charcoal.png",
      // The ground carries no wordmark now, so the logo above renders over it.
      heroWordmarkInImage: false,
      colors: {
        // Warm charcoal, chosen by the owner 2026-08-19 to replace the navy-teal
        // this was originally sampled from their logo. Warm (a brown undertone)
        // rather than a true grey, because the rest of the palette is warm —
        // cream paper and gold accent — and a cool grey makes that cream read
        // yellow. Their logo mark stays navy-teal: it is the barn's own artwork,
        // it sits on the light surfaces, and the cream reverse logo already
        // covers the dark ones.
        primary: "#3A3632",
        primaryDark: "#23201C",
        accent: "#CE9B3A",      // derived warm-gold complement (prices / CTAs / live)
        ink: "#17232A",
        paper: "#F4EEE2",       // warm cream (pairs with navy-teal)
      },
      iosBundleId: "com.beavercountystockyards.app",
      androidPackage: "com.beavercountystockyards.app",
      // The Play developer account the app is published under — Google's
      // 2026-10-27 policy email was addressed "Hi Developers at RS Livestock
      // LLC". Printed on the barn's privacy / deletion / support pages so they
      // name the listing's developer as well as the app. Confirm it against
      // the listing's "Developer" line if that ever changes.
      storeDeveloperName: "RS Livestock LLC",
    },
    features: ["barn_page", "my_barn", "schedule", "weather", "ask", "futures", "push"],
    // Owner call 2026-10-08: Beaver's app shows the FEEDER business only, for
    // now. They do run a cow/bull sale every Monday (25 of them on record), so
    // this is a deliberate scoping of the app, not a fact about the barn —
    // dropping both lines restores the full picture.
    priceBoardTypes: ["Steers", "Heifers"],
    saleTypes: ["feeder"],
    customModules: [],
    contact: {
      phone: "(580) 625-3051",
      website: "https://www.beaverstockyards.com",
      address: "506 W. Main St, Beaver, OK 73932",
    },
    // ⚠️ WAS `/events/131006`, COMMENTED "persistent DVAuction channel" — DEAD,
    // and shipped dead. Verified 2026-10-02: it redirects to DVAuction's
    // homepage. Replaced with the schedule, which lists Beaver by name and date
    // (checked the same day: 10/6, 10/13, 10/20 and on to 12/8) and so cannot
    // rot. This is the FALLBACK now — Beaver sets the actual sale link itself in
    // My Barn → Live Sale Link. Do not put an /events/ address here again.
    dvauctionUrl: "https://www.dvauction.com/schedule",
    facebookUrl: "https://www.facebook.com/beaverstockyards",
    // Verified live 2026-08-13: 26.5K followers, 337.5K likes, bio matches the
    // barn (Beaver OK, sale every Tuesday). Checked in a real browser — TikTok
    // serves a JS shell to anything else, so a curl status check cannot tell a
    // live profile from a dead one.
    tiktokUrl: "https://www.tiktok.com/@bcstockyards",
    // Resolved 2026-08-14 from the ownerChannelName on two videos the barn had
    // already pasted into the app (aUE-HUxtCLg, NdgkTT-hx-I) — both report
    // "Beaver County Stockyards". Their channel ran five videos in the three
    // days to 08-13 while the in-app Studio list sat on 07-24.
    youtubeChannelId: "UCyxgI0c-3M9g8nBfeCYF4-g",
    textAlerts: { keyword: "BCS", number: "5802077083" }, // Text BCS to (580) 207-7083
    show: { title: "BCS Studio", tagline: "Recorded at the barn" }, // coverSrc: add the studio photo when supplied
    // `feedertop` is "Leaderboard" for EVERY barn since 2026-10-13 (owner: "on
    // all barn apps top feeders name needs to be changed to leaderboard"), so
    // Beaver's old override is gone. History, because it was a considered
    // call: Beaver first named it "Leaderboard" themselves; the 2026-08-14
    // audit (audits/barn-app-top-feeders-2026-08-14.md) added "Feeder" because
    // the board starts at 500 lb and a bare "Leaderboard" promises the best
    // with no weight qualifier — their real top was $725.00 on calves under
    // 500 lb against the $532.50 the board maxes out at. The qualifier now
    // lives on the screen itself ("500 lb and up" under the title and in the
    // list layout's sub line), not on the tile.
    //
    // General lesson for the factory: `tileLabels` can rename any deliberately
    // scoped tool to a general-sounding name and defeat its own disclosure.
    // Check the sub-header before renaming a tile for a barn.
    // TikTok pinned FIRST — Beaver has 26.5K followers and 337.5K likes, which is
    // very likely a larger audience than this app will ever have. Prominence is a
    // per-barn call for exactly that reason; a barn with 200 followers should not
    // get a billboard. Reorder or drop "tiktok" here to change it — no code.
    tilesFirst: ["tiktok", "feedertop", "ticker"],
    // Their newest video leads the home screen. Earned, not decorative: twelve
    // uploads in the five days to 2026-08-18, four of them that day, against a
    // once-a-day refresh for every market source we carry. Their channel is the
    // liveliest thing in this app.
    //
    // ⚠️ NOT YET AGREED BY THE BARN (as of 2026-08-18) — this is what we're
    // showing them. `?videohero=0` renders the classic tools-first home for the
    // same barn, so both can be shown side by side. If they'd rather keep the
    // classic layout, set this to false; nothing else changes.
    videoHero: true,
    // The partner lockup also runs under the header of the DVAuction-data tools,
    // not just on Home. Beaver only for now — see partnerCredit for why this is
    // opt-in rather than a default.
    partnerCredit: true,
    // ⚠️ ON, AND IT HAD TO BE ON BEFORE THE FIRST STORE SUBMISSION.
    //
    // Gate 2 of two (gate 1 is `autoUpdate: false` in the Capacitor config).
    // Flipping this is itself a CODE change, so it reaches phones through a
    // store build — OTA cannot bootstrap itself. Ship Beaver's v1 with it off
    // and turning it on later costs a brand-new store submission, which is the
    // exact cost this whole piece exists to avoid (BARN_APP_OTA_SCOPE.md).
    //
    // On is safe with nothing published: the app pins itself to channel
    // `barn-beaver-county`, asks what is latest, and gets nothing. Errors are
    // swallowed to a console warning — a barn app that cannot reach the update
    // server must still open and show today's sale. The failure mode is
    // "updates quietly stop", never "a bad bundle installs".
    ota: { enabled: true },
  },

  // Barn #2 — Mitchell Livestock Marketing (Mitchell, SD). Full-service SD barn
  // (strong cow/bull + feeder). Brand: white distressed-western wordmark → navy
  // (#1C3A57) + harvest gold accent; app icon = the wordmark's "M" monogram.
  // Features MATCH beaver-county for full parity.
  "mitchell-livestock": {
    slug: "mitchell-livestock",
    webPath: "mitchell",                      // raisedbeef.com/mitchell
    layout: "grid",                           // the standard — see beaver-county
    barnName: "Mitchell Livestock Marketing", // verified exact DB name 2026-07-22
    state: "SD",
    brand: {
      displayName: "Mitchell Livestock Marketing",
      shortName: "Mitchell Livestock",
      tagline: "Mitchell, South Dakota",
      logoSrc: "/barn-apps/mitchell-livestock/logo-hd.png",              // navy art (recolored from their white wordmark), for light surfaces
      logoReverseSrc: "/barn-apps/mitchell-livestock/logo-reverse-hd.png", // cream art, for navy chrome / dark masthead
      // heroSrc omitted → shared cattle hero (swap for a Mitchell photo later)
      colors: {
        primary: "#1C3A57",     // Mitchell navy-blue
        primaryDark: "#122A40",
        accent: "#C6902F",      // harvest gold (prices / CTAs / live)
        ink: "#14232A",
        paper: "#EFEDE6",
      },
      iosBundleId: "com.mitchelllivestock.app",
      androidPackage: "com.mitchelllivestock.app",
    },
    features: ["barn_page", "my_barn", "schedule", "weather", "ask", "futures", "push"],
    // Owner call 2026-11-02: feeders only, like Beaver. Mitchell DOES run a big
    // cow/bull sale every Wednesday (~600 head) beside its Thursday feeder sale
    // (up to 3,000 head, 2026-10-22) — this scopes the app, it is not a fact
    // about the barn. Dropping both lines restores the full picture, including
    // the Cows/Bulls price board that was added for Mitchell on 2026-08-14.
    priceBoardTypes: ["Steers", "Heifers"],
    saleTypes: ["feeder"],
    customModules: [],
    contact: {
      phone: "(605) 996-6543",
      website: "https://www.mitchelllivestockauction.com",
      address: "1801 E Spruce St, Mitchell, SD 57301",
    },
    dvauctionUrl: "https://www.nationalbeefwire.com/channels/6077-mitchell-livestock-marketing",
    facebookUrl: "https://www.facebook.com/MitchellLivestockMarketingLlc",
    // Verified 2026-08-14 two independent ways: a web search for the barn, and
    // resolving their handle @mitchelllivestcockmarketing (their own spelling —
    // the correctly-spelled handle 404s) to externalId. Channel is titled
    // "Mitchell Livestock Marketing" and carries 30+ videos. Their website links
    // no YouTube at all, so this came from the channel, not from them.
    youtubeChannelId: "UCCCxZJxutfU5KVL8QO6lQvw",
  },

  // ── SALES DEMO TENANT (owner request 2026-08-02) ──────────────────────────
  // Generic-branded app for demoing the white-label product to prospective
  // barns. Identical experience to Beaver County; the DATA comes from a real
  // high-volume barn (Joplin Regional — the densest tape on DVAuction, twice-
  // weekly sales) while every DISPLAYED name is the neutral demo brand. The
  // source barn's name never renders: headers/wordmark come from `brand`
  // (logoSrc null → text wordmark), and the leak audit 2026-08-02 confirmed no
  // component prints a barn name from data. "ask" is deliberately excluded —
  // the front-desk assistant would speak the real barn's name. NEVER ship this
  // to a store; demo via ?barn=demo or a VITE_BARN_APP=demo build only.
  "demo": {
    slug: "demo",
    demoTenant: true,                      // renders a real barn's data under a neutral brand
    layout: "grid",                        // the standard — a demo must look like what they'd get
    barnName: "Joplin Regional Stockyards", // DATA source — verified exact DB name 2026-08-02 (13k lots/90d)
    state: "MO",
    brand: {
      displayName: "Your Sale Barn",
      shortName: "Your Barn",
      tagline: "Your brand, your sale, live",
      logoSrc: null,          // text wordmark fallback — "their logo goes here" is the pitch
      logoReverseSrc: null,
      colors: {
        primary: "#24435C",   // neutral navy — reads premium without claiming a brand
        primaryDark: "#16293A",
        accent: "#CE9B3A",    // warm gold (prices / CTAs / live)
        ink: "#1F2730",
        paper: "#F4F0E6",
      },
      iosBundleId: "com.raisedbeef.barndemo",     // placeholder — never store-shipped
      androidPackage: "com.raisedbeef.barndemo",
    },
    // Beaver parity minus "ask" (name leak — see block comment).
    features: ["barn_page", "my_barn", "schedule", "weather", "futures", "push"],
    customModules: [],
    // contact/dvauctionUrl/facebookUrl/textAlerts/show intentionally omitted —
    // each would surface the real barn's identity or a real barn's channels.
    // No-empty-screens rule for demos: every tile a prospect taps must be
    // full. `video` (no clips under the demo tenant) and `salesheet` (no
    // consignment lineup) are trimmed; everything else runs on Joplin's tape.
    tiles: [
      "consign", "schedule", "topfive", "ticker", "calftracker", "feedertop",
      "records", "topnational", "wavg", "feederflash", "dvauction", "newswire",
      "priceboard", "forecast", "weather", "futures", "contact",
      // ("buyeralerts"/"lotalerts" were listed here and did nothing — they are
      //  Settings rows, not tiles. Both still show in the demo, via Settings.)
    ],
  },

  // ⚠️ ONE APP, SIX YARDS — NOT SIX APPS. See `yards` on BarnAppConfig for the
  // Guideline 4.3 reasoning; the short version is that six near-identical apps
  // from one company's developer account is the clone pattern Apple rejects,
  // and one app with a picker has no clone pattern at all.
  //
  // The slug stays "bluegrass-lexington" and the web address with it. Both are
  // load-bearing: the address is already published and links get texted and
  // bookmarked, and the bundle ids below can never change once a build is
  // uploaded. Renaming them to "bluegrass" would buy tidiness and cost a dead
  // link — so the comment carries the meaning instead.
  "bluegrass-lexington": {
    slug: "bluegrass-lexington",
    webPath: "bluegrass-lexington",
    layout: "grid",   // raisedbeef.com/bluegrass-lexington
    // The DEFAULT yard — Lexington, their largest at 16,782 head over 90 days.
    // activeBarnApp() replaces this with whichever yard is selected.
    barnName: "Bluegrass Stockyards", // verified against dv_sale_market_reports
    state: "KY",
    // All six verified against dv_sale_market_reports 2026-09-25, ordered by
    // head over 90 days — biggest first, because the picker's first entry is
    // the one most customers want and the default when nothing is chosen.
    //
    // ⚠️ THE LABELS ARE BLUEGRASS'S OWN NAMES, FROM THEIR "Locations" MENU —
    // not our abbreviations of the legal names in barnName. Their site calls
    // these yards "Mt. Sterling – East" and "Stanford – South"; we had shipped
    // "East" and "South", which are the suffixes of the DATABASE names and are
    // not what their own customers call the places. A rancher picks the TOWN
    // he hauls to. Check any new label against bluegrassstockyards.com rather
    // than deriving it from barnName.
    //
    // ⚠️ SEVEN, NOT SIX — AND THE SEVENTH IS WHY YOU START FROM THEIR SITE.
    // We built this list by searching the sale data for "bluegrass", which
    // silently missed "Farmers Stockyards Inc." because the company's name is
    // nowhere in it. Their own Locations menu has it. The app would have gone
    // in front of their CEO missing one of his barns, and nothing in the code
    // or the data would have said so — a list that is short is indistinguishable
    // from a list that is complete.
    yards: [
      // ⚠️ phone + website are EACH YARD'S OWN, quoted from that yard's page
      // on bgstockyards.com on 2026-09-25. Street addresses are still absent
      // because the barn publishes none — same reason the company block below
      // omits one. The Contact screen's city and map pin come from the
      // geocode (keyed on barnName), so those already follow the yard.
      { id: "lexington",      barnName: "Bluegrass Stockyards",                        label: "Lexington",           state: "KY",
        phone: "(859) 255-7701", website: "https://bgstockyards.com/locations/lexington/" },
      { id: "south",          barnName: "Bluegrass South Livestock Market",            label: "Stanford – South",    state: "KY",
        phone: "(606) 365-0665", website: "https://bgstockyards.com/locations/stanford-south/" },
      { id: "richmond",       barnName: "Bluegrass Stockyards of Richmond, LLC",       label: "Richmond",            state: "KY",
        phone: "(859) 623-1280", website: "https://bgstockyards.com/locations/richmond/" },
      { id: "campbellsville", barnName: "Bluegrass Stockyards of Campbellsville, LLC", label: "Campbellsville",      state: "KY",
        phone: "(270) 465-4051", website: "https://bgstockyards.com/locations/campbellsville/" },
      { id: "albany",         barnName: "Bluegrass Stockyards of Albany",              label: "Albany",              state: "KY",
        phone: "(606) 387-4681", website: "https://bgstockyards.com/locations/albany/" },
      // ⚠️ THE ONE WITHOUT "BLUEGRASS" IN ITS NAME. It is theirs — it is on
      // their Locations menu — but every search we ran for this barn used
      // `ilike '%bluegrass%'`, so it was invisible to us until the owner
      // compared the picker against their website. 4,022 hd/90d, selling
      // through 2026-09-22. When the next multi-yard company arrives, take the
      // list from the BARN'S OWN SITE first and match it to the data; never
      // build the list by searching the data for the company's name.
      { id: "farmers",        barnName: "Farmers Stockyards Inc.",                     label: "Farmers Stockyard",   state: "KY",
        phone: "(606) 845-2421", website: "https://bgstockyards.com/locations/farmers-stockyard/" },
      { id: "east",           barnName: "Bluegrass Stockyards East, LLC",              label: "Mt. Sterling – East", state: "KY",
        phone: "(859) 498-9625", website: "https://bgstockyards.com/locations/mt-sterling-east/" },
    ],
    brand: {
      displayName: "Bluegrass Stockyards",
      shortName: "Bluegrass",
      // Bluegrass's own logo, supplied by the owner 2026-09-25.
      //
      // ⚠️ logo-reverse-hd IS NOT USABLE FOR THIS BARN, and that is why the
      // masthead carries the wordmark instead. The reverse is generated by
      // painting one flat colour through the artwork's alpha — fine for a
      // single-colour logo, wrong for this one: 27% of its ink is WHITE (the
      // letter outlines, the rosette disc) and the cattle inside are BLACK.
      // Flattened to cream, the rosette becomes a featureless disc and the
      // three cattle vanish. Measured, not guessed.
      logoSrc: "/barn-apps/bluegrass-lexington/logo-hd.png",
      logoReverseSrc: "/barn-apps/bluegrass-lexington/logo-reverse-hd.png",
      // ⚠️ THE HERO CARRIES THE LOGO, so heroWordmarkInImage below is true and
      // the app does NOT draw one over it. This is the way out of navy-on-navy:
      // their mark is navy and the masthead is navy, so a logo laid on the hero
      // would disappear and a recoloured one would not be their logo any more.
      //
      // Verified through the REAL path, not eyeballed at full size: the asset
      // is cover-cropped to 1600x900 by scripts/barn-images.mjs, then
      // cover-cropped AGAIN by CSS (background-position: center 56%) into a
      // masthead that is min-height:226px at 375–430px wide. Both extremes
      // rendered and checked — 80px comes off the sides on the narrowest
      // phone, 48px off the top and bottom on the widest. The logo clears both.
      // Keep any replacement's artwork inside the middle ~70% or it clips.
      heroSrc: "/barn-apps/bluegrass-lexington/masthead.jpg",
      heroWordmarkInImage: true,
      colors: {
        primary: "#002C5A",
        primaryDark: "#001B3A",
        accent: "#C08A2E",
        ink: "#17232A",
        paper: "#F1EEE6",
      },
      iosBundleId: "com.bluegrasslexington.app",
      androidPackage: "com.bluegrasslexington.app",
    },
    // "ask" is ON as of 2026-09-30, in the same change that wrote this barn's
    // fact sheet (supabase/functions/ask-barn/facts.ts) — never before, which
    // is the rule that kept it off here for five days. askBarnFacts.test.ts
    // fails the build if the two ever come apart again.
    features: ["barn_page", "my_barn", "schedule", "weather", "ask", "futures", "push"],
    contact: {
      phone: "(859) 255-7701",
      // ⚠️ /lexington 404s — their location pages live under /locations/.
      // Dead for this barn in practice, because activeBarnApp() overrides this
      // with the selected yard's own website, and all seven yards carry a
      // correct URL. Fixed rather than deleted: the override only replaces
      // fields a yard defines, so this is the fallback if a yard ever loses
      // its own link.
      website: "https://bgstockyards.com/locations/lexington/",
      // ⚠️ Address deliberately omitted — NOT ONE of the barn's seven location
      // pages publishes a street address (re-checked 2026-09-30); they carry
      // phone, fax, email and a manager's cell. A wrong address in a barn's
      // own app is worse than none. Fill from the intake form.
    },
    // Verified live 2026-09-30: ~28.6K likes, bio reads "Bluegrass Stockyards |
    // Lexington KY", and it is the handle their own site links in its footer.
    // A LINK, not an embed — same stance as every other barn, so none of
    // Facebook's tracking enters the app and the store privacy answers already
    // filed stay true.
    facebookUrl: "https://www.facebook.com/bgstockyards",
    customModules: [],
  },

  "coffeyville-stockyards": {
    slug: "coffeyville-stockyards",
    webPath: "coffeyville-stockyards",
    layout: "grid",   // raisedbeef.com/coffeyville-stockyards
    // Verified against dv_sale_market_reports 2026-08-15 and again 2026-10-13
    // (10,064 lots / 39,461 head / 63 sale days, last sale 2026-10-08). The
    // "(North Town)" is part of the name DVAuction publishes under, so it stays.
    barnName: "Coffeyville Stockyards, LLC - KS (North Town)",
    state: "KS",
    brand: {
      displayName: "Coffeyville Stockyards",
      shortName: "Coffeyville",
      tagline: "Coffeyville, Kansas",
      // THE BARN'S OWN LOGO — black "Coffeyville", dark-red "Stockyards" and a
      // red steer-head outline, supplied by the owner 2026-10-13 on a white
      // ground; the white was knocked out and the ink un-premultiplied so the
      // edges keep their colour. The icon is the steer head alone, cut from
      // the same file (barns/assets/coffeyville-stockyards/mark.png).
      logoSrc: "/barn-apps/coffeyville-stockyards/logo-hd.png",
      logoReverseSrc: "/barn-apps/coffeyville-stockyards/logo-reverse-hd.png",
      // A two-colour logo on a dark ground loses its second colour (the cream
      // reverse is one ink), and black type on dark red is unreadable — so the
      // masthead is white with the real logo on it, as Flint Hills is.
      mastheadTone: "light",
      colors: {
        // The logo's own red, sampled from 74,722 ink pixels (#86000F mean).
        primary: "#86000F",
        primaryDark: "#5A000A",
        accent: "#C08A2E",
        ink: "#17232A",
        paper: "#F1EEE6",
      },
      iosBundleId: "com.coffeyvillestockyards.app",
      androidPackage: "com.coffeyvillestockyards.app",
    },
    // Full parity with Beaver. "ask" restored 2026-10-13 with the fact sheet
    // written from coffeyvillestockyards.com and the barn's own Facebook page
    // (supabase/functions/ask-barn/facts.ts).
    features: ["barn_page", "my_barn", "schedule", "weather", "ask", "futures", "push"],
    customModules: [],
    // From coffeyvillestockyards.com/contact-us, read 2026-10-13. The North
    // Town barn is the one in our market data. (DVAuction's seller listing
    // gives 910 W 14th St / PO Box 1074; the barn's own contact page says 822 W
    // 14th Street — the barn's own page wins.)
    contact: {
      phone: "(620) 251-5460",
      website: "https://www.coffeyvillestockyards.com",
      address: "822 W 14th Street, Coffeyville, KS 67337",
    },
    // A search link, not an /events/ id, so it cannot rot the way an event
    // address does (same pattern as Flint Hills). The barn sets the actual
    // sale link itself in My Barn → Live Sale Link.
    dvauctionUrl: "https://www.dvauction.com/search?query=Coffeyville",
    facebookUrl: "https://www.facebook.com/coffeyvillestockyards",
  },

  "emporia-livestock": {
    slug: "emporia-livestock",
    webPath: "emporia-livestock",
    layout: "grid",   // raisedbeef.com/emporia-livestock
    // Verified against dv_sale_market_reports 2026-08-15 and again 2026-10-13
    // (1,331 lots / 7,344 head / 30 sale days, last sale 2026-10-07). A THIN
    // barn: one Wednesday sale a week, 8–55 lots, feeders, cows and bulls in
    // the same sale — the app will show small numbers, which is the barn.
    barnName: "Emporia Livestock Sales",
    state: "KS",
    brand: {
      displayName: "Emporia Livestock Sales",
      shortName: "Emporia",
      tagline: "Emporia, Kansas · since 1941",
      // THE BARN'S OWN LOGO — "EAT BEEF" over a script "Emporia Livestock
      // Sales" with a steer-head outline, supplied by the owner 2026-10-13 as
      // WHITE ON BLACK, plus the steer head on its own as a second file. The
      // ink was lifted off the black (alpha = how white the pixel is) and
      // painted charcoal for the light surfaces; the cream reverse carries the
      // dark ones. Icon = the separate steer-head file.
      logoSrc: "/barn-apps/emporia-livestock/logo-hd.png",
      logoReverseSrc: "/barn-apps/emporia-livestock/logo-reverse-hd.png",
      // Flat charcoal, as Beaver's: their artwork is white on black, so the
      // masthead is that black with the cream logo on it, not a photo with a
      // veil. (barns/assets/emporia-livestock/masthead.jpg is a solid fill.)
      heroSrc: "/barn-apps/emporia-livestock/masthead.jpg",
      heroWordmarkInImage: false,
      colors: {
        // Their logo has no hue (white on black), so charcoal, as Flint Hills.
        primary: "#22262A",
        primaryDark: "#14171A",
        accent: "#C08A2E",
        ink: "#17232A",
        paper: "#F1EEE6",
      },
      iosBundleId: "com.emporialivestock.app",
      androidPackage: "com.emporialivestock.app",
    },
    // Full parity with Beaver. "ask" restored 2026-10-13 with the fact sheet
    // written from emporialivestock.com (supabase/functions/ask-barn/facts.ts).
    features: ["barn_page", "my_barn", "schedule", "weather", "ask", "futures", "push"],
    customModules: [],
    // From emporialivestock.com/contact.php, read 2026-10-13.
    contact: {
      phone: "(620) 342-2425",
      website: "https://emporialivestock.com",
      address: "502 Albert Street, Emporia, KS 66801",
    },
    // A search link, not an /events/ id, so it cannot rot (same pattern as
    // Flint Hills and Coffeyville). The barn sets the actual sale link itself
    // in My Barn → Live Sale Link.
    dvauctionUrl: "https://www.dvauction.com/search?query=Emporia",
    facebookUrl: "https://www.facebook.com/EmporiaLivestock/",
  },

  "flint-hills-stockyards": {
    slug: "flint-hills-stockyards",
    webPath: "flint-hills-stockyards",
    layout: "grid",   // raisedbeef.com/flint-hills-stockyards
    barnName: "Flint Hills Stockyards", // verified against dv_sale_market_reports
    state: "KS",
    brand: {
      displayName: "Flint Hills Stockyards",
      shortName: "Flint Hills",
      tagline: "St. Marys, Kansas",
      // THE BARN'S OWN LOGO — the FHS monogram + wordmark, supplied by the owner
      // 2026-10-08 (pure black, no brand hue). The icon is the monogram alone,
      // cut from the same file (barns/assets/flint-hills-stockyards/mark.png).
      logoSrc: "/barn-apps/flint-hills-stockyards/logo-hd.png",
      logoReverseSrc: "/barn-apps/flint-hills-stockyards/logo-reverse-hd.png",
      // Owner, 2026-10-08: "We can just have a white header with this logo."
      // No photo, no veil — the black logo on a white ground. See mastheadTone.
      mastheadTone: "light",
      colors: {
        primary: "#22262A",
        primaryDark: "#14171A",
        accent: "#C08A2E",
        ink: "#17232A",
        paper: "#F1EEE6",
      },
      iosBundleId: "com.flinthillsstockyards.app",
      androidPackage: "com.flinthillsstockyards.app",
    },
    // Full parity with Beaver. "ask" restored 2026-10-08 with the fact sheet
    // written from flinthillsstockyards.com (supabase/functions/ask-barn/facts.ts).
    features: ["barn_page", "my_barn", "schedule", "weather", "ask", "futures", "push"],
    // Owner call 2026-11-02: feeders only. Every Flint sale in the last year was
    // a feeder sale, so today this changes nothing on screen; it guarantees it.
    priceBoardTypes: ["Steers", "Heifers"],
    saleTypes: ["feeder"],
    customModules: [],
    // From flinthillsstockyards.com, read 2026-10-08 (home, /upcoming-sales,
    // /seller-info, /buyer-info, /about-us, /contact-us).
    contact: {
      phone: "(785) 437-2785",
      website: "https://flinthillsstockyards.com",
      address: "27425 Drew Street, St Marys, KS 66536",
    },
    // The barn's own "Watch on DVAuction" link — a search, so it cannot rot the
    // way an /events/ address does. The barn sets the actual sale link itself
    // in My Barn → Live Sale Link.
    dvauctionUrl: "https://www.dvauction.com/search?query=St.+Marys",
    facebookUrl: "https://www.facebook.com/profile.php?id=61565223593853",
  },

  "fredonia-livestock": {
    slug: "fredonia-livestock",
    webPath: "fredonia-livestock",
    layout: "grid",   // raisedbeef.com/fredonia-livestock
    barnName: "Fredonia Livestock Auction", // verified against dv_sale_market_reports
    state: "KS",
    brand: {
      displayName: "Fredonia Livestock Auction",
      shortName: "Fredonia",
      logoSrc: null,          // text wordmark until this barn's artwork arrives
      logoReverseSrc: null,   // then: npm run barn:images fredonia-livestock
      // heroSrc omitted -> shared cattle hero
      colors: {
        primary: "#24384A",
        primaryDark: "#16232F",
        accent: "#C08A2E",
        ink: "#17232A",
        paper: "#F1EEE6",
      },
      iosBundleId: "com.fredonialivestock.app",
      androidPackage: "com.fredonialivestock.app",
    },
    features: ["barn_page", "my_barn", "schedule", "weather", "futures", "push"],
    customModules: [],
  },

  "green-city-livestock": {
    slug: "green-city-livestock",
    webPath: "green-city-livestock",
    layout: "grid",   // raisedbeef.com/green-city-livestock
    barnName: "Green City Livestock Marketing, LLC", // verified against dv_sale_market_reports
    state: "MO",
    brand: {
      displayName: "Green City Livestock",
      shortName: "Green City",
      logoSrc: null,          // text wordmark until this barn's artwork arrives
      logoReverseSrc: null,   // then: npm run barn:images green-city-livestock
      // heroSrc omitted -> shared cattle hero
      colors: {
        primary: "#004F88",
        primaryDark: "#003156",
        accent: "#C08A2E",
        ink: "#17232A",
        paper: "#F1EEE6",
      },
      iosBundleId: "com.greencitylivestock.app",
      androidPackage: "com.greencitylivestock.app",
    },
    features: ["barn_page", "my_barn", "schedule", "weather", "futures", "push"],
    customModules: [],
  },

  "hub-city-livestock": {
    slug: "hub-city-livestock",
    webPath: "hub-city-livestock",
    layout: "grid",   // raisedbeef.com/hub-city-livestock
    barnName: "Hub City Livestock Auction", // verified against dv_sale_market_reports
    state: "SD",
    brand: {
      displayName: "Hub City Livestock Auction",
      shortName: "Hub City",
      logoSrc: null,          // text wordmark until this barn's artwork arrives
      logoReverseSrc: null,   // then: npm run barn:images hub-city-livestock
      // heroSrc omitted -> shared cattle hero
      colors: {
        primary: "#1565C0",
        primaryDark: "#0D3F7A",
        accent: "#C08A2E",
        ink: "#17232A",
        paper: "#F1EEE6",
      },
      iosBundleId: "com.hubcitylivestock.app",
      androidPackage: "com.hubcitylivestock.app",
    },
    features: ["barn_page", "my_barn", "schedule", "weather", "futures", "push"],
    customModules: [],
  },

  "joplin-regional": {
    slug: "joplin-regional",
    webPath: "joplin-regional",
    layout: "grid",   // raisedbeef.com/joplin-regional
    barnName: "Joplin Regional Stockyards", // verified against dv_sale_market_reports
    state: "MO",
    brand: {
      displayName: "Joplin Regional Stockyards",
      shortName: "Joplin Regional",
      logoSrc: null,          // text wordmark until this barn's artwork arrives
      logoReverseSrc: null,   // then: npm run barn:images joplin-regional
      // heroSrc omitted -> shared cattle hero
      colors: {
        primary: "#6C1206",
        primaryDark: "#430A00",
        accent: "#C89A3C",
        ink: "#17232A",
        paper: "#F2EEE4",
      },
      iosBundleId: "com.joplinregional.app",
      androidPackage: "com.joplinregional.app",
    },
    features: ["barn_page", "my_barn", "schedule", "weather", "ask", "futures", "push"],
    customModules: [],
  },

  "kalona-sales-barn": {
    slug: "kalona-sales-barn",
    webPath: "kalona-sales-barn",
    layout: "grid",   // raisedbeef.com/kalona-sales-barn
    barnName: "Kalona Sales Barn", // verified against dv_sale_market_reports
    state: "IA",
    brand: {
      displayName: "Kalona Sales Barn",
      shortName: "Kalona",
      logoSrc: null,          // text wordmark until this barn's artwork arrives
      logoReverseSrc: null,   // then: npm run barn:images kalona-sales-barn
      // heroSrc omitted -> shared cattle hero
      colors: {
        primary: "#492621",
        primaryDark: "#2C1614",
        accent: "#C8A46A",
        ink: "#17232A",
        paper: "#F1EEE6",
      },
      iosBundleId: "com.kalonasalesbarn.app",
      androidPackage: "com.kalonasalesbarn.app",
    },
    features: ["barn_page", "my_barn", "schedule", "weather", "futures", "push"],
    customModules: [],
  },

  "kingsville-livestock": {
    slug: "kingsville-livestock",
    webPath: "kingsville-livestock",
    layout: "grid",   // raisedbeef.com/kingsville-livestock
    barnName: "Kingsville Livestock Auction", // verified against dv_sale_market_reports
    state: "MO",
    brand: {
      displayName: "Kingsville Livestock Auction",
      shortName: "Kingsville",
      logoSrc: null,          // text wordmark until this barn's artwork arrives
      logoReverseSrc: null,   // then: npm run barn:images kingsville-livestock
      // heroSrc omitted -> shared cattle hero
      colors: {
        primary: "#1F4633",
        primaryDark: "#122A1F",
        accent: "#C08A2E",
        ink: "#17232A",
        paper: "#F1EEE6",
      },
      iosBundleId: "com.kingsvillelivestock.app",
      androidPackage: "com.kingsvillelivestock.app",
    },
    features: ["barn_page", "my_barn", "schedule", "weather", "futures", "push"],
    customModules: [],
  },

  "landmark-mercantile": {
    slug: "landmark-mercantile",
    webPath: "landmark-mercantile",
    layout: "grid",   // raisedbeef.com/landmark-mercantile
    barnName: "Landmark Mercantile Livestock Auction", // verified against dv_sale_market_reports
    state: "NM",
    brand: {
      displayName: "Landmark Mercantile",
      shortName: "Landmark",
      logoSrc: null,          // text wordmark until this barn's artwork arrives
      logoReverseSrc: null,   // then: npm run barn:images landmark-mercantile
      // heroSrc omitted -> shared cattle hero
      colors: {
        primary: "#DF3131",
        primaryDark: "#8C1E1E",
        accent: "#C08A2E",
        ink: "#17232A",
        paper: "#F1EEE6",
      },
      iosBundleId: "com.landmarkmercantile.app",
      androidPackage: "com.landmarkmercantile.app",
    },
    features: ["barn_page", "my_barn", "schedule", "weather", "futures", "push"],
    customModules: [],
  },

  "napoleon-livestock": {
    slug: "napoleon-livestock",
    webPath: "napoleon-livestock",
    layout: "grid",   // raisedbeef.com/napoleon-livestock
    barnName: "Napoleon Livestock", // verified against dv_sale_market_reports
    state: "ND",
    brand: {
      displayName: "Napoleon Livestock",
      shortName: "Napoleon",
      logoSrc: null,          // text wordmark until this barn's artwork arrives
      logoReverseSrc: null,   // then: npm run barn:images napoleon-livestock
      // heroSrc omitted -> shared cattle hero
      colors: {
        primary: "#116DFF",
        primaryDark: "#0A4299",
        accent: "#C08A2E",
        ink: "#17232A",
        paper: "#F1EEE6",
      },
      iosBundleId: "com.napoleonlivestock.app",
      androidPackage: "com.napoleonlivestock.app",
    },
    features: ["barn_page", "my_barn", "schedule", "weather", "futures", "push"],
    customModules: [],
  },

  "ogallala-livestock": {
    slug: "ogallala-livestock",
    webPath: "ogallala-livestock",
    layout: "grid",   // raisedbeef.com/ogallala-livestock
    // Verified against dv_sale_market_reports 2026-08-15 and again 2026-10-14
    // (10,254 lots / 70,372 head / 43 sale days, last sale 2026-10-08). Two
    // sale days: WEDNESDAY weigh-up (cows, bulls, bred/stock heifers, 130–510
    // head) and THURSDAY special stocker & feeder sales when advertised
    // (1,000–5,900 head, roughly every other week).
    barnName: "Ogallala Livestock Auction Market",
    state: "NE",
    brand: {
      displayName: "Ogallala Livestock Auction",
      shortName: "Ogallala",
      tagline: "Ogallala, Nebraska",
      // THE BARN'S OWN LOGO — a dark-red rope script "Ogallala" over a serif
      // LIVESTOCK AUCTION MARKET, supplied by the owner 2026-10-14 on a white
      // ground, plus the rope "O" on its own as the mark. The white was knocked
      // out and the ink un-premultiplied; the rope's white highlights become
      // transparent, which is why the masthead and icon grounds are white.
      logoSrc: "/barn-apps/ogallala-livestock/logo-hd.png",
      logoReverseSrc: "/barn-apps/ogallala-livestock/logo-reverse-hd.png",
      // White masthead (as Flint Hills and Coffeyville): the rope art reads
      // as rope only with its highlights showing white.
      mastheadTone: "light",
      colors: {
        // The logo's own red, sampled from 193,318 solid ink pixels (#820200
        // mean). The site-CSS red (#E74C3C) that stood in for it is retired.
        primary: "#820200",
        primaryDark: "#5A0100",
        accent: "#C08A2E",
        ink: "#17232A",
        paper: "#F1EEE6",
      },
      iosBundleId: "com.ogallalalivestock.app",
      androidPackage: "com.ogallalalivestock.app",
    },
    // Full parity with Beaver. "ask" restored 2026-10-14 with the fact sheet
    // written from ogallalalive.com (supabase/functions/ask-barn/facts.ts).
    features: ["barn_page", "my_barn", "schedule", "weather", "ask", "futures", "push"],
    customModules: [],
    // From ogallalalive.com (home page), read 2026-10-14.
    contact: {
      phone: "(308) 284-2071",
      website: "https://www.ogallalalive.com",
      address: "1507 West 1st Street, Ogallala, NE 69153",
    },
    // A search link, not an /events/ id, so it cannot rot (same pattern as
    // Flint Hills, Coffeyville and Emporia). The barn sets the actual sale
    // link itself in My Barn → Live Sale Link.
    dvauctionUrl: "https://www.dvauction.com/search?query=Ogallala",
    facebookUrl: "https://www.facebook.com/ogallalalivestockauctionmarket/",
  },

  "springfield-livestock": {
    slug: "springfield-livestock",
    webPath: "springfield-livestock",
    layout: "grid",   // raisedbeef.com/springfield-livestock
    barnName: "Springfield Livestock Marketing Center", // verified against dv_sale_market_reports
    state: "MO",
    brand: {
      displayName: "Springfield Livestock Marketing Center",
      shortName: "Springfield",
      logoSrc: null,          // text wordmark until this barn's artwork arrives
      logoReverseSrc: null,   // then: npm run barn:images springfield-livestock
      // heroSrc omitted -> shared cattle hero
      colors: {
        primary: "#DF3131",
        primaryDark: "#8C1E1E",
        accent: "#C08A2E",
        ink: "#17232A",
        paper: "#F1EEE6",
      },
      iosBundleId: "com.springfieldlivestock.app",
      androidPackage: "com.springfieldlivestock.app",
    },
    features: ["barn_page", "my_barn", "schedule", "weather", "futures", "push"],
    customModules: [],
  },

  "st-onge-livestock": {
    slug: "st-onge-livestock",
    webPath: "st-onge-livestock",
    layout: "grid",   // raisedbeef.com/st-onge-livestock
    // Verified against dv_sale_market_reports 2026-08-15 and again 2026-10-14
    // (7,557 lots / 41,691 head / 32 sale days, last sale 2026-10-10). A big
    // FRIDAY barn: the regular cattle sale every Friday (fall calf specials
    // run 2,400–5,000 head; Oct 9 = 5,004), Saturday weigh-up / bred-cow
    // specials when advertised (Oct 10 = 999 head, 799 of them cows), and the
    // odd mid-week special (Wed Oct 7 = a 1,256-head spring calf special).
    //
    barnName: "St. Onge Livestock",
    state: "SD",
    // St. Onge is in MOUNTAIN time (western SD) while barnTimezone() maps SD
    // to Central by state. Found readying the barn 2026-10-14; this field was
    // added the same day so "today", live polling and lot times run on the
    // barn's own clock.
    timezone: "America/Denver",
    brand: {
      displayName: "St. Onge Livestock",
      shortName: "St. Onge",
      tagline: "St. Onge, South Dakota",
      // THE BARN'S OWN LOGO — a black silhouette (the wordmark over a line of
      // riders, horses and a corral, with a faint gold rim light), supplied by
      // the owner 2026-10-14 as a transparent PNG. The icon is the big rider
      // in the middle of the scene, cut from the same file below the wordmark
      // (barns/assets/st-onge-livestock/mark.png).
      logoSrc: "/barn-apps/st-onge-livestock/logo-hd.png",
      logoReverseSrc: "/barn-apps/st-onge-livestock/logo-reverse-hd.png",
      // A black silhouette on a dark ground is nothing; white masthead, as
      // Flint Hills (whose logo is also black).
      mastheadTone: "light",
      colors: {
        // No hue in the artwork beyond the gold rim, which the house accent
        // already matches. Charcoal, as Flint Hills; the navy sampled from the
        // site's CSS in August is retired.
        primary: "#22262A",
        primaryDark: "#14171A",
        accent: "#C08A2E",
        ink: "#17232A",
        paper: "#F1EEE6",
      },
      iosBundleId: "com.stongelivestock.app",
      androidPackage: "com.stongelivestock.app",
    },
    // Full parity with Beaver. "ask" restored 2026-10-14 with the fact sheet
    // written from stongelivestock.com (supabase/functions/ask-barn/facts.ts).
    features: ["barn_page", "my_barn", "schedule", "weather", "ask", "futures", "push"],
    customModules: [],
    // From stongelivestock.com (home + /about/), read 2026-10-14.
    contact: {
      phone: "(605) 642-2200",
      website: "https://stongelivestock.com",
      address: "11693 Sale Barn Rd, St. Onge, SD 57779",
    },
    // A search link, not an /events/ id, so it cannot rot (same pattern as
    // the other barns readied this week). The barn sets the actual sale link
    // itself in My Barn → Live Sale Link.
    dvauctionUrl: "https://www.dvauction.com/search?query=St.+Onge",
    // The barn's page covers both businesses: "St. Onge Livestock & Newell
    // Sheep Yards".
    facebookUrl: "https://www.facebook.com/p/St-Onge-Livestock-Newell-Sheep-Yards-100088028534981/",
  },

  "stockmens-exchange-east": {
    slug: "stockmens-exchange-east",
    webPath: "stockmens-exchange-east",
    layout: "grid",   // raisedbeef.com/stockmens-exchange-east
    barnName: "Stockmen's Livestock Exchange – EAST", // verified against dv_sale_market_reports
    state: "ND",
    brand: {
      displayName: "Stockmen's Livestock Exchange",
      shortName: "Stockmen's",
      logoSrc: "/barn-apps/stockmens-exchange-east/logo-hd.png",              // black art on transparency, for light surfaces
      logoReverseSrc: "/barn-apps/stockmens-exchange-east/logo-reverse-hd.png", // cream art (recolored from logo-hd), for dark chrome / masthead
      // heroSrc omitted -> shared cattle hero
      colors: {
        // Their logo is solid black line art on transparency and carries no
        // second color, so the chrome takes its cue from the artwork the same
        // way Torrington's did: near-black chrome that lets the cream reverse
        // logo carry the masthead. The accent is the red off the barn's other
        // Stockmen's mark — the only color the brand uses anywhere. (Replaced
        // the scaffold's placeholder maroon #7F070F/#4E0409, which was a guess.)
        primary: "#1C1C1C",
        primaryDark: "#101010",
        accent: "#C02030",
        ink: "#17232A",
        paper: "#F1EEE6",
      },
      iosBundleId: "com.stockmensexchangeeast.app",
      androidPackage: "com.stockmensexchangeeast.app",
    },
    // "ask" is on, and the fact sheet that makes it real ships with it
    // (supabase/functions/ask-barn/facts.ts). askBarnFacts.test.ts fails the
    // build if this feature is ever declared without a sheet — without one the
    // assistant answers "Quick answers aren't set up for this barn yet" to
    // every question, which is how barn #2 first shipped.
    features: ["barn_page", "my_barn", "schedule", "weather", "ask", "futures", "push"],
    customModules: [],
    contact: {
      phone: "(701) 225-8156",
      website: "http://www.gostockmens.com",
      address: "815 Livestock Ln, Dickinson, ND 58601",
    },
    facebookUrl:
      "https://www.facebook.com/p/Stockmens-Livestock-Exchange-Inc-Dickinson-ND-100095025190101/",
  },

  "stockmens-yankton": {
    slug: "stockmens-yankton",
    webPath: "stockmens-yankton",
    layout: "grid",   // raisedbeef.com/stockmens-yankton
    barnName: "Stockmen's Livestock", // verified against dv_sale_market_reports
    state: "SD",
    brand: {
      displayName: "Stockmen's Livestock",
      shortName: "Stockmen's",
      logoSrc: null,          // text wordmark until this barn's artwork arrives
      logoReverseSrc: null,   // then: npm run barn:images stockmens-yankton
      // heroSrc omitted -> shared cattle hero
      colors: {
        primary: "#294877",
        primaryDark: "#182B48",
        accent: "#C08A2E",
        ink: "#17232A",
        paper: "#F1EEE6",
      },
      iosBundleId: "com.stockmensyankton.app",
      androidPackage: "com.stockmensyankton.app",
    },
    features: ["barn_page", "my_barn", "schedule", "weather", "futures", "push"],
    customModules: [],
  },

  "torrington-livestock": {
    slug: "torrington-livestock",
    webPath: "torrington-livestock",
    layout: "grid",   // raisedbeef.com/torrington-livestock
    barnName: "Torrington Livestock Markets", // verified against dv_sale_market_reports
    state: "WY",
    brand: {
      displayName: "Torrington Livestock Markets",
      shortName: "Torrington",
      logoSrc: "/barn-apps/torrington-livestock/logo-hd.png",              // black art (cream field keyed out), for light surfaces
      logoReverseSrc: "/barn-apps/torrington-livestock/logo-reverse-hd.png", // cream art (recolored from logo-hd), for dark chrome / masthead
      // heroSrc omitted -> shared cattle hero
      colors: {
        // Their logo is black ink on a cream field and carries no other color,
        // so the chrome takes its cue from the artwork rather than inventing one:
        // charcoal darks that let the cream reverse logo carry the masthead, and
        // a brass accent for prices/CTAs. (Replaced the scaffold's placeholder
        // blue #334AFF/#1E2C99, which was a guess, not Torrington's color.)
        primary: "#2E3436",
        primaryDark: "#1B1E1F",
        accent: "#C08A2E",
        ink: "#17232A",
        paper: "#F1EEE6",
      },
      iosBundleId: "com.torringtonlivestock.app",
      androidPackage: "com.torringtonlivestock.app",
    },
    // "ask" enabled 2026-08-30, together with the fact sheet that makes it real
    // (supabase/functions/ask-barn/facts.ts). It was deliberately OFF from the
    // 08-15 scaffold until then: the assistant with no sheet answers "Quick
    // answers aren't set up for this barn yet" to every question, which is how
    // barn #2 shipped. src/barnApps/askBarnFacts.test.ts now fails the build if
    // this feature is ever declared without a sheet.
    features: ["barn_page", "my_barn", "schedule", "weather", "ask", "futures", "push"],
    customModules: [],
    contact: {
      phone: "(307) 532-3333",
      website: "https://torringtonlivestock.com",
      address: "626 West Valley Rd., Torrington, WY 82240",
    },
    facebookUrl: "https://www.facebook.com/TorringtonLivestock",
    // Resolved 2026-08-30 from the handle the barn gave us (@TorringtonLivestock).
    // Verified against YouTube's own canonical binding: the handle page's og:url
    // is youtube.com/channel/UCXOI5gEPmqDpUjaRu-NnOpg, channel titled "Torrington
    // Livestock". Also mirrored into public.barn_channel_sync so a new upload can
    // push — see migration 20260818000001 for why the id lives in both places.
    youtubeChannelId: "UCXOI5gEPmqDpUjaRu-NnOpg",
    // NO videoHero, and deliberately so — owner decision 2026-08-30: the home
    // screen stays tools-first for every barn but Beaver, and video lives on the
    // barn's own Video page. Beaver's hero was earned by twelve uploads in five
    // days; this channel has ~16 videos in three years, 25 subscribers, and has
    // been dormant since 2026-04-10 — and the newest upload is titled "test", so
    // newest-first would have led their home screen with it. Same reasoning
    // applies to `show`: a branded Studio tab implies a program this channel is
    // not. The tile reads "Torrington Video" from shortName and needs no config.
    // Revisit both if their posting habit changes.
  },

  "unionville-livestock": {
    slug: "unionville-livestock",
    webPath: "unionville-livestock",
    layout: "grid",   // raisedbeef.com/unionville-livestock
    barnName: "Unionville Livestock Market", // verified against dv_sale_market_reports
    state: "MO",
    brand: {
      displayName: "Unionville Livestock Market",
      shortName: "Unionville",
      logoSrc: null,          // text wordmark until this barn's artwork arrives
      logoReverseSrc: null,   // then: npm run barn:images unionville-livestock
      // heroSrc omitted -> shared cattle hero
      colors: {
        primary: "#E45E26",
        primaryDark: "#A03C13",
        accent: "#7A4A22",
        ink: "#17232A",
        paper: "#F1EEE6",
      },
      iosBundleId: "com.unionvillelivestock.app",
      androidPackage: "com.unionvillelivestock.app",
    },
    features: ["barn_page", "my_barn", "schedule", "weather", "futures", "push"],
    customModules: [],
  },

  "valentine-livestock": {
    slug: "valentine-livestock",
    webPath: "valentine-livestock",
    layout: "grid",   // raisedbeef.com/valentine-livestock
    barnName: "Valentine Livestock Auction", // verified against dv_sale_market_reports
    state: "NE",
    brand: {
      displayName: "Valentine Livestock Auction",
      shortName: "Valentine",
      logoSrc: null,          // text wordmark until this barn's artwork arrives
      logoReverseSrc: null,   // then: npm run barn:images valentine-livestock
      // heroSrc omitted -> shared cattle hero
      colors: {
        primary: "#B12420",
        primaryDark: "#6E1614",
        accent: "#C08A2E",
        ink: "#17232A",
        paper: "#F1EEE6",
      },
      iosBundleId: "com.valentinelivestock.app",
      androidPackage: "com.valentinelivestock.app",
    },
    features: ["barn_page", "my_barn", "schedule", "weather", "futures", "push"],
    customModules: [],
  },

  "stateline-stockyards": {
    slug: "stateline-stockyards",
    webPath: "stateline-stockyards",   // raisedbeef.com/stateline-stockyards
    barnName: "Stateline Stockyards", // verified against dv_sale_market_reports
    state: "OK",
    layout: "grid",
    brand: {
      displayName: "Stateline Stockyards",
      shortName: "Stateline",
      // ⚠️ PROVISIONAL ARTWORK, 2026-09-23 — NOT the barn's own files.
      // Derived from two images the owner supplied so the app could be looked
      // at on a phone. The barn has NOT sent a logo: their website carries only
      // a 196px PNG, and both source images here appear to be generated
      // approximations rather than Stateline's real brand file. Replace all of
      // it with the barn's originals before any store submission — this app
      // ships under THEIR developer account with THEIR name on it.
      //
      // logo-hd is the gold artwork lifted off its black ground by luminance,
      // so the alpha is real and the cream reverse recolors correctly.
      logoSrc: "/barn-apps/stateline-stockyards/logo-hd.png",              // gold art, alpha keyed from the black field
      logoReverseSrc: "/barn-apps/stateline-stockyards/logo-reverse-hd.png", // cream art, recolored through that alpha
      heroSrc: "/barn-apps/stateline-stockyards/masthead.jpg",
      // The masthead already carries the wordmark, so the app must not print
      // the name a second time on top of it.
      heroWordmarkInImage: true,
      // Sampled from the barn's own logo (supplied 2026-09-23): a brass
      // wordmark on black, inside the Oklahoma state outline. The near-black
      // is warm rather than the usual slate, so it sits with the gold instead
      // of fighting it.
      colors: {
        primary: "#15120E",
        primaryDark: "#0A0806",
        accent: "#C9A15E",
        ink: "#17232A",
        paper: "#F5F0E6",
      },
      iosBundleId: "com.statelinestockyards.app",
      androidPackage: "com.statelinestockyards.app",
    },
    features: ["barn_page", "my_barn", "schedule", "weather", "ask", "futures", "push"],
    customModules: [],
    contact: {
      phone: "580-609-3344",
      website: "https://www.statelinestockyards.com",
      address: "Burlington, OK",
    },
    facebookUrl: "https://www.facebook.com/p/Stateline-Stockyards-61579758035883/",
    show: { title: "Weekly Sale", tagline: "Thursdays at 9:00 AM" },
  },

  "mo-kan-livestock": {
    slug: "mo-kan-livestock",
    webPath: "mo-kan-livestock",   // raisedbeef.com/mo-kan-livestock
    layout: "grid",
    // Verified against dv_sale_market_reports 2026-11-03 (9,406 lots / 24,413
    // head / 43 sale days, last sale 2026-10-29). ⚠️ The capital O is how the
    // market data spells it, so it stays here. The barn writes "Mo-Kan", which
    // is what every screen shows, via brand.displayName.
    barnName: "MO-Kan Livestock Market",
    state: "MO",
    brand: {
      displayName: "Mo-Kan Livestock Market",
      shortName: "Mo-Kan",
      tagline: "Butler, Missouri",
      // ⚠️ PLACEHOLDER ARTWORK, cut 2026-11-03 from the logo on the barn's public
      // site (barns/assets/mo-kan-livestock/). Replace with the barn's originals
      // before any store build. The icon is the full MO/KAN lockup: the steer head
      // alone was too low-resolution in that file to read.
      logoSrc: "/barn-apps/mo-kan-livestock/logo-hd.png",
      // ⚠️ THE FULL-COLOUR LOGO, a copy of logo-hd, not the generated cream. One
      // ink flattened this logo to a blob; its own white keyline and black shadow
      // hold up on dark. `barn:images --force` overwrites it: see the launch doc.
      logoReverseSrc: "/barn-apps/mo-kan-livestock/logo-reverse-hd.png",
      // White header, as Flint Hills and Coffeyville. The logo is blue and red
      // with a black shadow; the cream reverse is one ink and would flatten it
      // to a silhouette. See mastheadTone.
      mastheadTone: "light",
      colors: {
        primary: "#063AA4",
        primaryDark: "#04266B",
        accent: "#C08A2E",
        ink: "#17232A",
        paper: "#F1EEE6",
      },
      iosBundleId: "com.mokanlivestock.app",
      androidPackage: "com.mokanlivestock.app",
    },
    // Full parity with Beaver. "ask" ships with the fact sheet written from
    // mo-kanlivestock.com (supabase/functions/ask-barn/facts.ts).
    features: ["barn_page", "my_barn", "schedule", "weather", "ask", "futures", "push"],
    // Feeder-focused like Flint Hills, with one deliberate difference: "mixed"
    // stays in. Mo-Kan's one weekly sale sells every class, so the classifier
    // calls some ordinary Thursdays `mixed` (6 of 28 Thursdays in the year to
    // 2026-11-03, up to 283 head of steers and heifers). ["feeder"] alone would
    // drop those sales from the app. `cow_bull` is still hidden.
    priceBoardTypes: ["Steers", "Heifers"],
    saleTypes: ["feeder", "mixed"],
    customModules: [],
    // From mo-kanlivestock.com /contact, read 2026-11-03.
    contact: {
      phone: "(660) 679-6535",
      website: "https://www.mo-kanlivestock.com",
      address: "7133 NE Business 49 Loop, Butler, MO 64730",
    },
    // A search, so it cannot rot the way an /events/ address does. Checked
    // 2026-11-03: lists the barn's events through January 2027. The barn sets the
    // actual sale link itself in My Barn → Live Sale Link.
    dvauctionUrl: "https://www.dvauction.com/search?query=Mo-Kan",
    // Facebook, YouTube and the text list are all linked from the barn's own
    // /text-app page, read 2026-11-03.
    facebookUrl: "https://www.facebook.com/mokanlivestock",
    youtubeChannelId: "UCtA58vqsSg9rSlF5ZzujR0g",
    textAlerts: { keyword: "CATTLE", number: "6602856060" }, // Text CATTLE to (660) 285-6060
  },

  // <barn-generator:insert-above> — scripts/new-barn.mjs inserts new barns here.
  // Keep this marker as the LAST line inside BARN_APPS.
};


// Beaver County — DVAuction edition. The scoped-down launch app DVAuction ships:
// the icon-tile grid, same navy-teal / warm-gold / cream identity and the SAME
// Beaver data (barnName+state unchanged → shares sales, push, My Barn, alerts),
// but only DVAuction's requested core tile set. Everything else stays in the
// codebase and is held back as "premium, added later" — flip a tool back on by
// adding its key to `tiles`. Order below mirrors DVAuction's own tool doc.
// `feedertop` is "Leaderboard" per their naming — since 2026-10-13 the default
// for every barn (it already renders the top-5-by-weight-class board they
// asked for; see the note on beaver-county). Own store bundle → its own app.
BARN_APPS["beaver-county-dvauction"] = {
  ...BARN_APPS["beaver-county"],
  slug: "beaver-county-dvauction",
  webPath: undefined, // see beaver-county-grid — inherited by spread otherwise
  // Same reason as webPath: the spread would inherit Beaver's video hero, and
  // this edition is a DIFFERENT PARTY'S app — deliberately scoped to a core tile
  // set, with the rest held back as "premium, added later". Putting a video
  // billboard at the top of it is DVAuction's call to make, not one they should
  // arrive at because Beaver's entry sits above this one in the file.
  videoHero: false,
  // Not inherited from Beaver by the spread, same reasoning as videoHero above:
  // where a partner's own credit appears inside a partner's own edition is that
  // partner's call.
  partnerCredit: false,
  // ⚠️ OFF, AND EXPLICITLY — the fourth thing this spread inherits that it must
  // not. Beaver was switched on 2026-09-27 and this edition silently came with
  // it, caught by the allowlist in ota.test.ts within a minute.
  //
  // It matters more than the other three. This is a DIFFERENT PARTY'S APP with
  // its own bundle id (com.beavercountystockyards.dvauction), and `ota.enabled`
  // is the switch that makes us able to push code onto that app's users'
  // phones. Inheriting that from the barn whose entry happens to sit above this
  // one in the file is not a decision anybody made.
  //
  // AND IT STAYS OFF: the owner confirmed 2026-09-27 that this edition is not
  // being used ("we won't be using that one"), consistent with DVAuction being
  // retired. Nothing to enable. Left explicit rather than deleted because the
  // spread would silently re-inherit Beaver's value the moment anyone removed
  // this line — the off switch has to be written down to stay off.
  ota: { enabled: false },
  layout: "grid",
  brand: {
    ...BARN_APPS["beaver-county"].brand,
    iosBundleId: "com.beavercountystockyards.dvauction",
    androidPackage: "com.beavercountystockyards.dvauction",
  },
  tiles: [
    "video",       // 1. Watch Live (DVAuction embed)
    "dvauction",   // DVAuction.com homepage (in-app browser)
    "newswire",    // NationalBeefWire.com homepage (in-app browser)
    "futures",     // 2. Future
    "topfive",     // 4. Top 5
    "feederflash", // 5. Feeder Flash (Corbitt Wall daily video)
    "show",        // 6. BCS Videos
    "ticker",      // 7. Beaver Ticker
    "calftracker", // 8. Calf Tracker
    "records",     // 9. BCS Records
    "salesheet",   // 10. Consignments
    "topnational", // 11. Top Sales
    "feedertop",   // 12. Leaderboard
    "weather",     // 13. Weather
    "textalerts",  // 15. Text Alerts
    "facebook",    // 16. BCS Facebook
    "salereport",  // 17. Market Reports
    // Deferred (added later): 14. BCS Exchange (link), 18. Online Chat.
  ],
  // No tileLabels: "Leaderboard" is the default label for `feedertop`.
  //
  // tilesFirst is NOT inherited. The base pins ["tiktok", "feedertop", "ticker"],
  // but this edition's `tiles` allow-list deliberately leaves TikTok out (owner
  // decision — it does not ship in the DVAuction edition yet). Inheriting the
  // pin left this config naming a tile the edition can never show: it reads as
  // configured, does nothing, and is invisible in the UI. Re-declare without it.
  tilesFirst: ["feedertop", "ticker"],
};


// Mitchell Livestock — DVAuction edition, the twin of beaver-county-dvauction:
// same Mitchell data and brand, the grid launchpad, scoped to DVAuction's core
// tile set with everything else held back as "premium, added later".
//
// ⚠️ SPECULATIVE. The DVAuction edition is a commercial arrangement that was
// agreed for BEAVER's launch; nobody has said DVAuction is shipping a Mitchell
// app. This entry exists so the version can be SHOWN (?barn=mitchell-livestock-dvauction)
// alongside the other two. The bundle ids below follow the established pattern
// but are not registered with any store — and a store id is permanent, so
// confirm the arrangement before submitting under them.
BARN_APPS["mitchell-livestock-dvauction"] = {
  ...BARN_APPS["mitchell-livestock"],
  slug: "mitchell-livestock-dvauction",
  webPath: undefined,
  layout: "grid",
  brand: {
    ...BARN_APPS["mitchell-livestock"].brand,
    iosBundleId: "com.mitchelllivestock.dvauction",
    androidPackage: "com.mitchelllivestock.dvauction",
  },
  tiles: [...(BARN_APPS["beaver-county-dvauction"].tiles ?? [])],
};

// ─── Active-tenant resolution ────────────────────────────────────────────────

/** The barn this build is locked to, or `null` when not a barn-app build
 *  (i.e. the normal RaisedBeef platform build).
 *
 *  Resolution order:
 *   1. Build-time `VITE_BARN_APP=<slug>` — how a real per-barn native build
 *      locks itself to one barn (production).
 *   2. `?barn=<slug>` URL param — a preview override so any deploy can show a
 *      barn's app without a separate build (mirrors the `?site=home` pattern).
 *      Public barn data only, so this is safe to expose.
 *   3. First path segment matching a barn's `webPath` — the PUBLIC web home
 *      page, e.g. raisedbeef.com/mitchell. This is the link a barn texts its
 *      customers; on desktop it renders inside a phone frame with the store
 *      buttons beside it (BarnWebFrame).
 *
 *  Order matters: a locked per-barn build (1) must never be overridable by a
 *  URL, or a link could make Beaver's shipped app render as Mitchell.
 */

/**
 * Should this barn's home screen lead with its video?
 *
 * `cfg.videoHero` is the barn's real setting; `?videohero=1` / `?videohero=0`
 * overrides it for one page load so both layouts can be SHOWN to a barn before
 * they pick one. Deliberately a URL override rather than a second registry
 * entry: an "edition" in this codebase is a separate STORE APP — its own
 * permanent bundle id, native folder and listing (see beaver-county-dvauction) —
 * and a layout preference is not a different app. Minting an edition per layout
 * would leave a permanent store identity behind a question we're still asking.
 *
 * Same safety stance as `?barn=`: it only picks between two of our own layouts
 * over public barn data, so exposing it costs nothing.
 */
export function resolveVideoHero(cfg: Pick<BarnAppConfig, "videoHero">): boolean {
  if (typeof window !== "undefined") {
    const q = new URLSearchParams(window.location.search).get("videohero");
    if (q === "1" || q === "true") return true;
    if (q === "0" || q === "false") return false;
  }
  return cfg.videoHero === true;
}

/** First path segment, lowercased, no slashes — "" at the site root. */
function firstPathSegment(): string {
  if (typeof window === "undefined") return "";
  return window.location.pathname.split("/").filter(Boolean)[0]?.toLowerCase() ?? "";
}

/** The barn whose public web page is at this path, or null. Exported so the
 *  router and the prerender share ONE definition of "is this a barn address". */
export function barnAppSlugForPath(segment: string): string | null {
  const seg = segment.trim().toLowerCase();
  if (!seg) return null;
  for (const cfg of Object.values(BARN_APPS)) {
    // Opt-in ONLY: a barn is reachable on the web when it has been given a
    // webPath deliberately. Falling back to `slug` would silently publish
    // every tenant — including the demo brands, which must never be a public
    // address on the marketing domain.
    if (!cfg.webPath) continue;
    if (cfg.webPath.toLowerCase() === seg) return cfg.slug;
  }
  return null;
}

/** True when this page is a barn app shown ON THE WEB — either at the barn's
 *  public address (raisedbeef.com/mitchell) or via a ?barn= preview. Both get
 *  the desktop phone frame.
 *
 *  The ONE case that must never be framed is the shipped native build: it is
 *  already inside a phone, and drawing a second one would be absurd.
 *
 *  ?barn= previews are framed too, deliberately — that is how a layout variant
 *  (…-grid, …-dvauction) or a sales demo is shown on a laptop, and a phone
 *  around it is the whole point of showing it. `?frame=0` opts out for anyone
 *  who wants the old full-window behaviour. */
export function isBarnWebHome(): boolean {
  const built = (import.meta.env?.VITE_BARN_APP as string | undefined)?.trim();
  if (built) return false;
  if (typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("frame") === "0") return false;
    if (params.get("barn")?.trim()) return true;
  } catch {
    /* ignore */
  }
  return barnAppSlugForPath(firstPathSegment()) !== null;
}

export function activeBarnAppSlug(): string | null {
  const built = (import.meta.env?.VITE_BARN_APP as string | undefined)?.trim();
  if (built && built.length > 0) return built;
  if (typeof window !== "undefined") {
    try {
      const q = new URLSearchParams(window.location.search).get("barn")?.trim();
      // A `?barn=` that names a barn we no longer have must NOT win over the
      // path. Links get texted, bookmarked and pasted into notes, and they
      // outlive registry entries: removing the `-grid` layout variants on
      // 2026-08-15 turned an owner's saved
      // `/mitchell?barn=mitchell-livestock-grid` into a blank screen, because
      // the override resolved to nothing while still counting as "this is a
      // barn app". Falling through to the path means a stale override
      // degrades to the right barn instead of to nothing.
      if (q && q.length > 0 && BARN_APPS[q]) return q;
      if (q && q.length > 0) {
        console.warn(`[barnApps] ?barn="${q}" is not a barn — falling back to the path.`);
      }
    } catch {
      /* ignore */
    }
    const fromPath = barnAppSlugForPath(firstPathSegment());
    if (fromPath) return fromPath;
  }
  return null;
}

/**
 * Which yard of a multi-yard barn the customer is looking at.
 *
 * ⚠️ PER DEVICE, NOT PER ACCOUNT, AND THAT IS DELIBERATE. Most of a barn app's
 * users are ranchers who never sign in; there is no account to hang a
 * preference on. A man who hauls to Richmond wants Richmond when he opens the
 * app tomorrow, and localStorage is the only thing that survives that on a
 * phone.
 *
 * ⚠️ AND IT IS READ THROUGH A TRY/CATCH. A private window, cleared site data
 * or a locked-down browser makes localStorage throw on ACCESS, not just return
 * null — and this runs on every render of every screen in the app.
 */
const YARD_KEY = "raisedbeef.barnapp.yard";

export function selectedYardId(cfg: BarnAppConfig): string | null {
  if (!cfg.yards?.length) return null;
  try {
    const saved = window.localStorage.getItem(`${YARD_KEY}.${cfg.slug}`);
    if (saved && cfg.yards.some((y) => y.id === saved)) return saved;
  } catch {
    /* no storage — fall through to the default */
  }
  return cfg.yards[0].id;
}

/** Remember the yard. Silent on failure: a preference is not worth an error. */
export function setSelectedYard(cfg: BarnAppConfig, id: string): void {
  if (!cfg.yards?.some((y) => y.id === id)) return;
  try {
    window.localStorage.setItem(`${YARD_KEY}.${cfg.slug}`, id);
  } catch {
    /* ignore */
  }
}

/** The resolved config for this build, or `null` on the platform build. */
export function activeBarnApp(): BarnAppConfig | null {
  const slug = activeBarnAppSlug();
  if (!slug) return null;
  const cfg = BARN_APPS[slug];
  if (!cfg) {
    // Fail loud in dev; a mis-set flavor must never silently ship the wrong barn.
    console.error(`[barnApps] VITE_BARN_APP="${slug}" has no registry entry.`);
    return null;
  }
  // ⚠️ A MULTI-YARD BARN RESOLVES TO ITS SELECTED YARD, RIGHT HERE.
  //
  // This is the whole trick, and it is why the yard picker cost one file
  // instead of sixty-four. Every screen in the app reads `cfg.barnName` off
  // this function — 64 of them — and not one needs to know yards exist,
  // because the config it receives already IS the yard the customer chose.
  // The `?name=` override just below is the same pattern, already proven.
  //
  // state travels with barnName: they are two halves of one identity, and a
  // Kentucky yard under another state's heading is the kind of quiet wrongness
  // this product cannot afford. Today all six are KY, which is exactly why it
  // must be carried rather than assumed — the next multi-yard company will
  // cross a state line and nothing here will warn us.
  if (cfg.yards?.length) {
    const id = selectedYardId(cfg);
    const yard = cfg.yards.find((y) => y.id === id) ?? cfg.yards[0];
    // ⚠️ CONTACT TRAVELS WITH THE YARD TOO, and it is not optional polish.
    //
    // The Contact screen takes its city and map pin from the geocode, which is
    // keyed on barnName and so already followed the yard. The phone and
    // website came from the company-level `contact` block and did NOT. That
    // left the screen HALF-switched — naming Richmond, pinning Richmond, and
    // dialling Lexington — which reads as correct and is the worst kind of
    // wrong. A man taps "Call the office" once and reaches the wrong barn.
    //
    // Only the fields the yard actually defines are replaced, so a yard with
    // no number of its own falls back to the company line rather than showing
    // nothing. barnYards.test.ts asserts they are all filled in for Bluegrass.
    return {
      ...cfg,
      barnName: yard.barnName,
      state: yard.state,
      contact: {
        ...cfg.contact,
        ...(yard.phone ? { phone: yard.phone } : {}),
        ...(yard.website ? { website: yard.website } : {}),
      },
    };
  }

  // DEMO-ONLY pitch personalization: ?barn=demo&name=Smith+Livestock puts the
  // prospect's name in the masthead and every "<shortName> …" tile label.
  // Locked to the demo tenants so a real barn's app can never be renamed by URL.
  if ((cfg.slug === "demo" || cfg.slug === "demo-grid") && typeof window !== "undefined") {
    try {
      const n = new URLSearchParams(window.location.search).get("name")?.trim().slice(0, 40);
      if (n) {
        return { ...cfg, brand: { ...cfg.brand, displayName: n, shortName: n } };
      }
    } catch {
      /* ignore */
    }
  }
  return cfg;
}

/**
 * The COMPANY's identity — the registry entry as written, before
 * activeBarnApp() swapped in the selected yard.
 *
 * ⚠️ MY BARN IS THE COMPANY'S DASHBOARD, NOT THE YARD'S, and it is the one
 * screen that must undo the yard resolution above.
 *
 * Everything else in a barn app resolves a slug through the SALE DATA
 * (mv_barn_names / dv_sale_market_reports), where all seven Bluegrass yards
 * exist and every yard therefore works. My Barn is the exception: it resolves
 * through `barn_accounts` + `barn_members`, and a multi-yard company has ONE
 * account row — the tenant is the company, and that is who bought the app.
 *
 * So on 2026-09-29 a real Bluegrass owner who had tapped Richmond in the yard
 * picker got "not linked": the dashboard asked for
 * `ky-bluegrass-stockyards-of-richmond-llc`, which is a perfectly good barn in
 * the sale data and no account anywhere. Six of the seven yards did this. The
 * yard picker is a view over the company's sale data; it is not a change of
 * tenant, and the dashboard must not read it as one.
 *
 * If per-yard dashboards are ever wanted, that is an account row per yard plus
 * a membership per yard — a data-model decision, not a slug tweak. Do not
 * reintroduce it by deriving the dashboard slug from the picker.
 */
export function companyBarnApp(cfg: BarnAppConfig): BarnAppConfig {
  const base = BARN_APPS[cfg.slug];
  if (!base || !cfg.yards?.length) return cfg;
  // brand and contact stay as resolved — the brand is company-level anyway, and
  // the yard's phone number is still the right number to show the person
  // holding the phone. Only the DATA identity goes back to the company.
  return { ...cfg, barnName: base.barnName, state: base.state };
}

/** True when this build is a single-barn white-label app. */
export function isBarnApp(): boolean {
  return activeBarnAppSlug() != null;
}
